import React, { Fragment, useEffect, useState } from 'react';
import { FormDateInput, FormRadio, FormSelect, FormNumericInput, FormDatePeriodSelector, FormHelperText, FormSwitch } from 'components/form-fields';
import moment from "moment";
import { ShowHide } from 'components/layout';
import { useValidation } from "hooks/useValidation";
import PropTypes from 'prop-types';
import classNames from 'classnames';
import 'components/settings/settings.css';

const defaultReoccurrenceSettings = {
    reoccurring: false,
    cycle: {
        type: 'repeat',
        frequency: 1,
        frequencyType: 'day',
        frequencyPeriodStart: null,
        frequencyPeriodOffset: -1,
        endType: 'never',
        endDate: null,
        endFrequency: 1
    }
};

export function ReoccurrenceSettings(
    {
        hide445 = false,
        hideEndDates = false,
        startDate = null,
        loading = false,
        disabled = false,

        reoccurrenceSettings = defaultReoccurrenceSettings,

        name = 'schedule',
        allowWeeklyRepeating = false,
        repeatEveryHeader = 'Repeat every',
        onReoccurrenceUpdate,
        onValidationUpdate,
        reoccurrenceFrequencyHelperText,
        reoccurrenceToggleText
    }
) {

    const reoccurrenceRepeatOptionList = [
        { id: "day", name: "Day(s)" },
        { id: "week", name: "Week(s)" },
        { id: "month", name: "Month(s)" },
        { id: "year", name: "Year(s)" },
    ];

    const [isValid, errors, validate] = useValidation();
    const [reoccurrenceRepeatOptions, setReoccurrenceRepeatOptions] = useState(reoccurrenceRepeatOptionList);

    const key445 = '445';
    const minFrequencyPeriod = 1;
    const maxFrequencyPeriod = 9999;
    const defaultEndDateYears = 2;
    const reoccurrenceFrequencyDefaults = {
        frequency: 1,
        frequencyType: "month"
    };

    useEffect(() => {
        let values = reoccurrenceRepeatOptionList;

        if (!allowWeeklyRepeating) {
            values = reoccurrenceRepeatOptionList.filter(a => a.id !== 'week');
        }

        setReoccurrenceRepeatOptions(values);

    }, [allowWeeklyRepeating]);

    const reoccurrenceOptions = [
        {
            value: "repeat", label: "Repeat every", onSelectRender: (repeatEveryFields())
        },
        {
            value: key445, label: "Every 4-4-5 period", onSelectRender: (
                <Fragment>
                    <p className="spacer para-text">When 4-4-5 is selected, by default we'll run the nearest period to your schedule start date. If you would like to add a delay to your 4-4-5 period you can do so by moving the dates back and we'll run the schedule with the delay each time the schedule is run. Please be aware if data has not been uploaded to Vision prior to the schedule running reports, these will be blank.</p>
                    <FormDatePeriodSelector startDate={startDate} periodType={key445} periodsToShow={1} initialPeriodOffset={reoccurrenceSettings.cycle != null ? reoccurrenceSettings.cycle.frequencyPeriodOffset : -1} maxPeriods={-1} minPeriods={-12} onChange={onFrequencyPeriodStartChange} disabled={disabled} />

                </Fragment>
            )
        }
    ];

    function repeatEveryFields(header) {
        return (
            <div className={classNames('form-row field-combination', { 'outer-validation': !!errors["cycle.frequency"] })}>
                <h4>{header}</h4>
                <FormHelperText loading={loading}>{reoccurrenceFrequencyHelperText}</FormHelperText>
                <FormNumericInput
                    disabled={disabled}
                    disabledInput={disabled}
                    min={minFrequencyPeriod}
                    max={maxFrequencyPeriod}
                    selectedValue={reoccurrenceSettings.cycle?.frequency}
                    onValueChange={onReoccurrenceFrequencyChange}
                    loading={loading}
                ></FormNumericInput>
                <FormSelect
                    disabled={disabled}
                    items={reoccurrenceRepeatOptions}
                    onItemSelect={onReoccurrenceFrequencyTypeChange}
                    selectedValue={reoccurrenceSettings.cycle?.frequencyType}
                    loading={loading}
                ></FormSelect>
                <FormHelperText loading={loading} danger={true}>{errors["cycle.frequency"]}</FormHelperText>
            </div>
        );
    }

    const endOptions = [
        { value: "never", label: "Never" },
        {
            value: "date", label: "On", onSelectRender: (
                <FormDateInput
                    id="reoccurrence-end-date"
                    onChange={onReoccurrenceEndDateChange}
                    value={reoccurrenceSettings.cycle?.endDate}
                    loading={loading}
                    minDate={startDate ? moment(startDate, "YYYY-MM-DD HH:mm").toDate() : null}
                    dangerHelperText={errors["cycle.endDate"]}
                    disabled={disabled}
                />
            )
        },
        {
            value: "occurrence", label: "After number of reoccurrences", onSelectRender: (
                <FormNumericInput
                    disabled={disabled}
                    min={minFrequencyPeriod}
                    max={maxFrequencyPeriod}
                    selectedValue={reoccurrenceSettings.cycle?.endFrequency}
                    onValueChange={onReoccurrenceEndFrequencyChange}
                    loading={loading}
                    dangerHelperText={errors["cycle.endFrequency"]}
                ></FormNumericInput>
            )
        }
    ];

    useEffect(onValidationChange, [isValid])

    function onValidationChange() {
        onValidationUpdate(isValid);
    }

    useEffect(updateValidationRules, [reoccurrenceSettings]);

    function updateValidationRules() {
        const rules = [
            { fieldName: "reoccurring", required: true }
        ];

        const externalRules = [];

        if (reoccurrenceSettings.reoccurring) {
            if (!hide445) {
                rules.push({ fieldName: "cycle.type", required: true });
            }
            if (!hideEndDates) {
                rules.push({ fieldName: "cycle.endType", required: true });

                if (reoccurrenceSettings.cycle.endType === "occurrence") {
                    rules.push({ fieldName: "cycle.endFrequency", required: true, minValue: minFrequencyPeriod, maxValue: maxFrequencyPeriod });
                }

                if (reoccurrenceSettings.cycle.endType === "date") {
                    rules.push({ fieldName: "cycle.endDate", required: true });
                }
            }

            if (reoccurrenceSettings.cycle.type === "repeat" || hide445) {
                rules.push({ fieldName: "cycle.frequency", required: true, type: "number", minValue: minFrequencyPeriod, maxValue: maxFrequencyPeriod });
            }
        }

        validate(rules, reoccurrenceSettings, externalRules);
    }

    function onStartDateChange() {
        //If the on end date is before the start date, then shift the end date on.
        if (reoccurrenceSettings && reoccurrenceSettings.cycle && reoccurrenceSettings.cycle.endDate && moment(reoccurrenceSettings.cycle.endDate).isBefore(startDate)) {
            onReoccurrenceEndDateChange(moment(startDate).add(defaultEndDateYears, "Y").format("YYYY-MM-DD"));
        }
    }

    useEffect(onStartDateChange, [startDate]);

    function onReoccurrenceChange(item) {
        const reoccurrence = {
            ...reoccurrenceSettings,
            reoccurring: item.currentTarget.checked
        };

        if (item && (!reoccurrence.cycle || !reoccurrence.cycle.frequency)) {
            reoccurrence.cycle = {
                type: 'repeat',
                frequency: 1,
                frequencyType: 'day',
                endType: 'never'
            };
        }

        onReoccurrenceUpdate(reoccurrence);
    }

    function onReoccurrencePatternChange(item) {

        const frequencyDefaults = item.currentTarget.value === key445 ? { frequencyPeriodOffset: -1 } : reoccurrenceFrequencyDefaults;

        const reoccurrence = {
            ...reoccurrenceSettings,
            cycle: {
                ...reoccurrenceSettings.cycle,
                ...frequencyDefaults,
                type: item.currentTarget.value,
            }
        }

        onReoccurrenceUpdate(reoccurrence);
    }

    function onReoccurrenceFrequencyChange(item) {

        const reoccurrence = {
            ...reoccurrenceSettings,
            cycle: {
                ...reoccurrenceSettings.cycle,
                frequency: item
            }
        }

        onReoccurrenceUpdate(reoccurrence);
    }

    function onReoccurrenceFrequencyTypeChange(item) {

        const reoccurrence = {
            ...reoccurrenceSettings,
            cycle: {
                ...reoccurrenceSettings.cycle,
                frequencyType: item.id
            }
        }

        onReoccurrenceUpdate(reoccurrence);
    }

    function onReoccurrenceEndChange(item) {

        const occurrenceCycle = {
            ...reoccurrenceSettings.cycle,
            endType: item.currentTarget.value
        };

        if (item.currentTarget.value === "date" && occurrenceCycle.endDate == null) {
            occurrenceCycle.endDate = moment(startDate).add(defaultEndDateYears, "Y").format("YYYY-MM-DD");
        }

        if (item.currentTarget.value === "occurrence" && occurrenceCycle.endFrequency == null) {
            occurrenceCycle.endFrequency = 10;
        }

        const reoccurrence = {
            ...reoccurrenceSettings,
            cycle: occurrenceCycle
        }

        onReoccurrenceUpdate(reoccurrence);
    }

    function onReoccurrenceEndFrequencyChange(item) {

        const reoccurrence = {
            ...reoccurrenceSettings,
            cycle: {
                ...reoccurrenceSettings.cycle,
                endFrequency: item
            }
        }

        onReoccurrenceUpdate(reoccurrence);
    }

    function onReoccurrenceEndDateChange(item) {

        const reoccurrence = {
            ...reoccurrenceSettings,
            cycle: {
                ...reoccurrenceSettings.cycle,
                endDate: item ? moment(item).format("YYYY-MM-DD") : null
            }
        }

        onReoccurrenceUpdate(reoccurrence);
    }

    function onFrequencyPeriodStartChange(item, endDate, offset) {

        const reoccurrence = {
            ...reoccurrenceSettings,
            cycle: {
                ...reoccurrenceSettings.cycle,
                frequencyPeriodStart: item ? moment(item, "DD/MM/YYYY", null).format("YYYY-MM-DD") : null,
                frequencyPeriodEnd: endDate ? moment(endDate, "DD/MM/YYYY", null).format("YYYY-MM-DD") : null,
                frequencyPeriodOffset: offset
            }
        }

        onReoccurrenceUpdate(reoccurrence);
    }

    return (
        <div>
            <FormSwitch
                id="reoccurrence"
                onChange={onReoccurrenceChange}
                headingText={!!reoccurrenceToggleText ?
                    reoccurrenceToggleText :
                    `Would you like your ${name} to reoccur?`}
                checked={reoccurrenceSettings.reoccurring}
                loading={loading}
                disabled={disabled}
            ></FormSwitch>
            <ShowHide
                evaluator={reoccurrenceSettings.reoccurring}
                show={(
                    <Fragment>
                        <ShowHide
                            evaluator={!hide445}
                            show={(
                                <FormRadio
                                    id="reoccurence-pattern"
                                    onChange={onReoccurrencePatternChange}
                                    headingText={`How often should this ${name} reoccur?`}
                                    selectedValue={reoccurrenceSettings.cycle?.type}
                                    options={reoccurrenceOptions}
                                    loading={loading}
                                    dangerHelperText={errors["cycle.type"]}
                                    disabled={disabled}
                                ></FormRadio>
                            )}
                        />

                        <ShowHide
                            evaluator={hide445}
                            show={repeatEveryFields(repeatEveryHeader)}
                        />

                        <ShowHide
                            evaluator={!hideEndDates}
                            show={(
                                <FormRadio
                                    id="reoccurence-end"
                                    onChange={onReoccurrenceEndChange}
                                    headingText={`When should this ${name} end?`}
                                    selectedValue={reoccurrenceSettings.cycle?.endType}
                                    options={endOptions}
                                    loading={loading}
                                    dangerHelperText={errors["cycle.endType"]}
                                    disabled={disabled}
                                ></FormRadio>
                            )}
                        />
                    </Fragment>
                )}
            />
        </div>
    );
}

ReoccurrenceSettings.propTypes = {
    hide445: PropTypes.bool,
    hideEndDates: PropTypes.bool,
    startDate: PropTypes.string,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    reoccurrenceSettings: PropTypes.object.isRequired,
    name: PropTypes.string,
    reoccurrenceToggleText: PropTypes.string,
    reoccurrenceFrequencyHelperText: PropTypes.string,
    allowWeeklyRepeating: PropTypes.bool,
    repeatEveryHeader: PropTypes.string,
    onReoccurrenceUpdate: PropTypes.func.isRequired,
    onValidationUpdate: PropTypes.func.isRequired
};
